import React from 'react';
import Modal from './Modal';
import { UserCircle, Users } from 'lucide-react';
import { useAuthContext } from '../../contexts/useAuthContext';
import { useI18n } from '@/i18n';

interface AIMatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTutor: () => void;
  onSelectParent: () => void;
}

const AIMatchModal: React.FC<AIMatchModalProps> = ({
  isOpen,
  onClose,
  onSelectTutor,
  onSelectParent,
}) => {
  const { profile } = useAuthContext();
  const { t } = useI18n();

  const isTutorIncomplete = profile?.role === 'tutor' && !profile?.agreedToTerms;
  const isParent = profile?.role === 'parent';

  // Auto-proceed for parent
  React.useEffect(() => {
    if (isOpen && isParent) {
      onSelectParent();
    }
  }, [isOpen, isParent, onSelectParent]);

  // Auto-proceed for tutor with incomplete profile
  React.useEffect(() => {
    if (isOpen && isTutorIncomplete) {
      onSelectTutor();
    }
  }, [isOpen, isTutorIncomplete, onSelectTutor]);

  // Don't show modal if user is parent or tutor with incomplete profile
  if (isParent || isTutorIncomplete) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('aiMatch.title')} maxWidth="max-w-md">
      <div className="text-center mb-6">
        <p className="text-gray-600">{t('aiMatch.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <button
          onClick={onSelectTutor}
          className="flex flex-col items-center justify-center p-8 border-2 border-gray-200 rounded-lg hover:border-primary hover:bg-primary-50 transition-all duration-200 group"
        >
          <UserCircle className="h-16 w-16 text-primary mb-4 group-hover:scale-110 transition-transform" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">{t('aiMatch.tutor.title')}</h3>
          <p className="text-gray-600 text-sm">{t('aiMatch.tutor.description')}</p>
        </button>

        <button
          onClick={onSelectParent}
          className="flex flex-col items-center justify-center p-8 border-2 border-gray-200 rounded-lg hover:border-secondary hover:bg-secondary-50 transition-all duration-200 group"
        >
          <Users className="h-16 w-16 text-secondary mb-4 group-hover:scale-110 transition-transform" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">{t('aiMatch.parent.title')}</h3>
          <p className="text-gray-600 text-sm">{t('aiMatch.parent.description')}</p>
        </button>
      </div>
    </Modal>
  );
};

export default AIMatchModal;
